import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Home, PhoneCall, Package, User, CalendarDays, Users, FileText,
} from 'lucide-react';

const HIDDEN_PATHS = [
  '/doctor/call/', '/psychology/join-call/', '/call/',
  '/permissions', '/install', '/login', '/register',
];

// Tabs per role. `tab` deep-links into the dashboard's tab via ?tab=.
const NAV_BY_ROLE = {
  patient: [
    { to: '/', icon: Home, ar: 'الرئيسية', en: 'Home', testId: 'bottomnav-home' },
    { to: '/doctors?category=doctors', icon: PhoneCall, ar: 'اتصال', en: 'Call', testId: 'bottomnav-call' },
    { to: '/subscriptions', icon: Package, ar: 'الباقات', en: 'Packages', testId: 'bottomnav-packages' },
    { to: '/patient', icon: User, ar: 'حسابي', en: 'Profile', testId: 'bottomnav-profile' },
  ],
  doctor: [
    { to: '/doctor?tab=calls', icon: PhoneCall, ar: 'المكالمات', en: 'Calls', tab: 'calls', testId: 'bottomnav-calls' },
    { to: '/doctor?tab=calendar', icon: CalendarDays, ar: 'التقويم', en: 'Calendar', tab: 'calendar', testId: 'bottomnav-calendar' },
    { to: '/doctor?tab=notes', icon: Users, ar: 'المرضى', en: 'Patients', tab: 'notes', testId: 'bottomnav-patients' },
    { to: '/doctor/profile', icon: User, ar: 'حسابي', en: 'Profile', testId: 'bottomnav-profile' },
  ],
  pharmacist: [
    { to: '/pharmacist?tab=calls', icon: PhoneCall, ar: 'المكالمات', en: 'Calls', tab: 'calls', testId: 'bottomnav-calls' },
    { to: '/pharmacist?tab=notes', icon: FileText, ar: 'الملاحظات', en: 'Notes', tab: 'notes', testId: 'bottomnav-notes' },
    { to: '/pharmacist?tab=profile', icon: User, ar: 'حسابي', en: 'Profile', tab: 'profile', testId: 'bottomnav-profile' },
  ],
};

/**
 * Native-app style bottom tab bar (phones only) for patients, doctors and
 * pharmacists. Adds `has-bottom-nav` to <body> so pages stay clear of it.
 */
export const BottomNav = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();

  const items = NAV_BY_ROLE[user?.role];
  const visible = !!items && !HIDDEN_PATHS.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    document.body.classList.toggle('has-bottom-nav', visible);
    return () => document.body.classList.remove('has-bottom-nav');
  }, [visible]);

  if (!visible) return null;

  const defaultTab = items.find((i) => i.tab)?.tab;
  const currentTab = new URLSearchParams(location.search).get('tab');

  const activeFor = (item) => {
    const [path] = item.to.split('?');
    if (item.tab) {
      return location.pathname === path && (currentTab ? currentTab === item.tab : item.tab === defaultTab);
    }
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-[800] bg-white/95 backdrop-blur border-t border-gray-200 pb-[env(safe-area-inset-bottom)]"
      data-testid="bottom-nav"
    >
      <ul className="flex items-stretch">
        {items.map((item) => {
          const Icon = item.icon;
          const active = activeFor(item);
          return (
            <li key={item.to} className="flex-1">
              <Link
                to={item.to}
                data-testid={item.testId}
                className={`h-16 flex flex-col items-center justify-center gap-1 transition-colors ${active ? 'text-[#0d47a1]' : 'text-gray-400'}`}
              >
                <span className={`flex items-center justify-center w-10 h-7 rounded-full transition-colors ${active ? 'bg-blue-50' : ''}`}>
                  <Icon size={20} strokeWidth={active ? 2.4 : 1.9} />
                </span>
                <span className="text-[11px] font-bold leading-none">{t(item.ar, item.en)}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default BottomNav;
